/**
 * FitnessProgramTemplateCard - Card component for displaying a fitness program template
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useRootStore } from '../../../Store/useRootStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { Theme } from '../../../Theme/Theme';
import { FitnessProgramTemplate, EQUIPMENT_VALUE_FREE, EQUIPMENT_VALUE_GYM } from '../../../Model/Coach/Program/Template/FitnessProgramTemplate';
import { getMediaLargeOrMediumOrSmallest } from '../../../Model/Media/Media';
import { getAthleteAllTags } from '../../../Model/Athlete/Athlete';

const Card = styled(Link)`
  display: block;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-decoration: none;
  color: inherit;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    text-decoration: none;
    color: inherit;
  }
`;

const ImageContainer = styled.div<{ $src?: string }>`
  position: relative;
  width: 100%;
  height: 180px;
  background: ${({ $src }) => ($src ? `url(${$src})` : Theme.Colors.lighterGrey)};
  background-size: cover;
  background-position: center;
`;

const ActiveBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: ${Theme.Colors.primary};
  color: white;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 6px 12px;
  border-radius: 20px;
`;

const LevelBadge = styled.div<{ $level: number }>`
  position: absolute;
  top: 12px;
  left: 12px;
  background: ${({ $level }) =>
    $level === 0 ? '#4CAF50' : $level === 1 ? '#FF9800' : '#F44336'};
  color: white;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 6px 12px;
  border-radius: 20px;
`;

const CardContent = styled.div`
  padding: 16px;
`;

const Title = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const Description = styled.p`
  font-size: 0.85rem;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 12px 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  color: ${Theme.Colors.charcoal};
`;

const MetaIcon = styled.span`
  font-size: 14px;
`;

export type FitnessProgramTemplateCardProps = {
  template: FitnessProgramTemplate;
  isActive?: boolean;
};

export const FitnessProgramTemplateCard: React.FC<FitnessProgramTemplateCardProps> = observer(
  ({ template, isActive }) => {
    const { t } = useTrans();
    const { authentication } = useRootStore();
    const title = useLocalized(template.name);
    const description = useLocalized(template.description);
    const image = template.getImage(authentication.athlete ? getAthleteAllTags(authentication.athlete) : []);
    const imageUrl = image ? getMediaLargeOrMediumOrSmallest(image) : undefined;

    const levelLabels = [
      t('fitnessPrograms.level.beginner', { defaultValue: 'Beginner' }),
      t('fitnessPrograms.level.intermediate', { defaultValue: 'Intermediate' }),
      t('fitnessPrograms.level.advanced', { defaultValue: 'Advanced' }),
    ];

    const equipmentLabels = {
      [EQUIPMENT_VALUE_FREE]: t('fitnessPrograms.equipment.none', { defaultValue: 'No Equipment' }),
      [EQUIPMENT_VALUE_GYM]: t('fitnessPrograms.equipment.gym', { defaultValue: 'Gym' }),
    };

    const weeksCount = template.microCyclesCount;
    const daysPerWeek = template.minDaysPerWeek === template.maxDaysPerWeek
      ? `${template.minDaysPerWeek}`
      : `${template.minDaysPerWeek}-${template.maxDaysPerWeek}`;

    return (
      <Card to={`/move/fitness-programs/${template.id}`}>
        <ImageContainer $src={imageUrl}>
          <LevelBadge $level={template.levelValue}>
            {levelLabels[template.levelValue]}
          </LevelBadge>
          {isActive && (
            <ActiveBadge>
              {t('fitnessPrograms.badge.active', { defaultValue: 'Active' })}
            </ActiveBadge>
          )}
        </ImageContainer>
        <CardContent>
          <Title>{title}</Title>
          {description && <Description>{description}</Description>}
          <MetaRow>
            <MetaItem>
              <MetaIcon>📅</MetaIcon>
              {weeksCount} {t('fitnessPrograms.meta.weeks', { defaultValue: 'weeks' })}
            </MetaItem>
            <MetaItem>
              <MetaIcon>🏋️</MetaIcon>
              {daysPerWeek}x / {t('fitnessPrograms.meta.week', { defaultValue: 'week' })}
            </MetaItem>
            {template.equipmentValue !== undefined && (
              <MetaItem>
                <MetaIcon>{template.equipmentValue === EQUIPMENT_VALUE_FREE ? '🏠' : '🏢'}</MetaIcon>
                {equipmentLabels[template.equipmentValue] || t('fitnessPrograms.equipment.custom', { defaultValue: 'Custom' })}
              </MetaItem>
            )}
          </MetaRow>
        </CardContent>
      </Card>
    );
  },
);
